import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getBookingReport } from '../../../../api/reportApi';
import ReportFilters from './ReportFilters';
import MetricsCards from './MetricsCards';
import BookingChart from './BookingChart';
import BookingPieChart from './BookingPieChart';
import RecentBookingsTable from './RecentBookingsTable';
import ExportButtons from './ExportButtons';
import '../css/Report.css'

const BookingReportDashboard = () => {
  const { t } = useTranslation();
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: null
  });

 const monthNames = useMemo(() => [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ], []);

 const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (filters.month) {
        // Fetch single month data
        const response = await getBookingReport({
          year: filters.year,
          month: filters.month
        });
        setReportData([{
          month: filters.month,
          monthName: monthNames[filters.month - 1],
          ...response
        }]);
      } else {
        // Fetch all months for the year
        const monthlyData = [];
        for (let month = 1; month <= 12; month++) {
          try {
            const response = await getBookingReport({
              year: filters.year,
              month
            });
            monthlyData.push({
              month,
              monthName: monthNames[month - 1],
              ...response
            });
          } catch {
            monthlyData.push({
              month,
              monthName: monthNames[month - 1],
              year: filters.year,
              totalBookings: 0,
              totalCompleted: 0,
              totalCancelled: 0,
              totalPending: 0,
              totalGuest: 0
            });
          }
        }
        setReportData(monthlyData);
      }
    } catch (err) {
      setError(t('admin.reports.failed_fetch'));
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, monthNames,t]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const getTotalMetrics = () => {
    return reportData.reduce((totals, month) => ({
      totalBookings: totals.totalBookings + (month.totalBookings || 0),
      totalCompleted: totals.totalCompleted + (month.totalCompleted || 0),
      totalCancelled: totals.totalCancelled + (month.totalCancelled || 0),
      totalPending: totals.totalPending + (month.totalPending || 0),
      totalGuest: totals.totalGuest + (month.totalGuest || 0)
    }), {
      totalBookings: 0,
      totalCompleted: 0,
      totalCancelled: 0,
      totalPending: 0,
      totalGuest: 0
    });
  };

  const hasData = reportData.some(month => month.totalBookings > 0);

  return (
    <div className="booking-report-dashboard">
      <div className="report-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h1 className="report-title">{t('admin.reports.dashboard_title')}</h1>
            <p className="report-subtitle">
              {t('admin.reports.dashboard_subtitle')}
            </p>
          </div>
          <div style={{ marginTop: '8px' }}>
            <ExportButtons 
              data={reportData} 
              filters={filters} 
              disabled={loading || !hasData}
            />
          </div>
        </div>
      </div>

      <ReportFilters 
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('admin.reports.loading_report')}</p>
        </div>
      )}

      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          {hasData ? (
            <>
              <MetricsCards metrics={getTotalMetrics()} />
              
              {/* Charts Section */}
              <div className="charts-section">
                <div className="charts-grid">
                  <div className="chart-column">
                    <BookingChart data={reportData} />
                  </div>
                  <div className="chart-column">
                    <BookingPieChart data={reportData} />
                  </div>
                </div>
              </div>

              {/* Recent Bookings Section */}
              <div className="recent-bookings-section">
                <RecentBookingsTable />
              </div>
            </>
          ) : (
            <div className="no-data-container">
              <div className="no-data-content">
                <h3>{t('admin.reports.no_data')}</h3>
                <p>
                  {t('admin.reports.no_data_for_period', {
                    period: filters.month ? monthNames[filters.month - 1] : t('admin.reports.filters.year'),
                    year: filters.year
                  })}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BookingReportDashboard;